/* One open product's areas (designs/products-pdp.md Screens 2–4, PP2,
   PP4–PP6, PP15–PP16): the Phase area's seven-step stepper with gate
   diamonds, the Phase complete bars (one per reached phase, a bar opens
   Deliverables filtered to its phase), the declared phase's exit criteria
   and the gate ahead; the Deliverables area listing every owed slot grouped
   swimlane → path with its state, its evidence, PP16's filters and counts,
   the Record evidence drawer and each row's history; the gate review sheet
   (Screen 5), the gate history with its certificate marks and the frozen
   certificate page (Screen 6). Everything is derived client-side from the
   product's record and the catalog — the writes are the drawer's PUT /
   DELETE and the sheet's POST. */
import { useEffect, useRef, useState, type JSX, type KeyboardEvent } from "react";
import type { Product } from "../../../shared/products";
import type { FeedRef, FeedSourceInfo, FeedTarget } from "../../../shared/feeds";
import {
  declaredPhase,
  feedSlotFor,
  groupOwedRows,
  owedCounts,
  owedRows,
  phaseBars,
  slotKey,
  slotRows,
  utcToday,
  type CertificateHead,
  type Evidence,
  type EvidenceInput,
  type GateReview,
  type GateReviewInput,
  type OwedCounts,
  type PhaseCertificate,
  type ProductPdp,
  type SlotKey,
  type SlotRow,
} from "../../../shared/product-pdp";
import { PDP_CRITERION_AREAS, PDP_PHASE_NAMES, PDP_PHASES, type PdpCriterion, type PdpGate, type PdpPhase } from "../../../shared/pdp";
import type { Role } from "../../../shared/roles";
import { useAppState, type Store } from "../shell/store";
import { ViewFrame } from "../shell/ViewFrame";
import { Loading } from "../shell/Loading";
import { LoadError } from "../shell/LoadError";
import { feedKey, type ClientState } from "../shell/state";
import { clearEvidence, fetchCertificate, readFeed, recordEvidence, recordGateReview } from "./products.actions";
import { Certificate } from "./products/Certificate";
import { EvidenceForm } from "./products/EvidenceForm";
import { FeedRowValue } from "./products/FeedRowValue";
import { GateReviewSheet, type ReviewRow } from "./products/GateReviewSheet";
import { DECISION_LABEL, STATE_LABEL } from "./products/model";
import { PhaseBars } from "./products/PhaseBars";
import { StateChip } from "./products/StateChip";
import { fmtDay, fmtStampDate, gateAfter, pdpFor, roleName } from "./products/pdpText";

const Missing = (): JSX.Element => <span className="state missing">not recorded</span>;

export function ProductAreaView(): JSX.Element {
  const { state, store } = useAppState();
  if (state.productsError) return <LoadError what="products" />;
  if (state.productsPending) return <Loading what="products" />;
  const product = state.products.find((p) => p.id === state.openProductId);
  /* a product the list stopped returning is not open any more — say so,
     exactly as a deleted project's or a vanished team's area page does */
  if (!product)
    return (
      <ViewFrame>
        <div className="area-note">
          <p>
            That product is no longer in the record. Use <b>File → View</b> to open another.
          </p>
        </div>
      </ViewFrame>
    );
  const pdp = pdpFor(state.productPdp, product.id);
  return (
    <ViewFrame
      footnote={
        <span>
          Every declaration is stamped with who recorded it and when; the owed set follows from the catalog and the
          declared phase alone.
        </span>
      }
    >
      {state.area === "Deliverables" ? (
        <DeliverablesArea product={product} pdp={pdp} state={state} store={store} />
      ) : (
        <PhaseArea product={product} pdp={pdp} state={state} store={store} />
      )}
    </ViewFrame>
  );
}

/* ======================= Phase ======================= */

/* the certificates still in force — a superseded one is history, not a mark */
const inForce = (certificates: CertificateHead[]): CertificateHead[] => certificates.filter((c) => c.supersededBy === null);

function PhaseArea({ product, pdp, state, store }: { product: Product; pdp: ProductPdp; state: ClientState; store: Store }): JSX.Element {
  const decl = pdp.declaration;
  const phase = declaredPhase(decl);
  const gate = phase === null ? undefined : gateAfter(state.pdp.gates, phase);
  const criteria = state.pdp.criteria
    .filter((c) => c.phase === phase)
    .sort((a, b) => PDP_CRITERION_AREAS.indexOf(a.area) - PDP_CRITERION_AREAS.indexOf(b.area));
  /* the owed rows, for each criterion's linked feed slot (KF13) */
  const owed = slotRows(owedRows(state.pdp.deliverables, phase), pdp.evidence);
  /* the cert-mark a closing certificate page hands focus back to */
  const [returnTo, setReturnTo] = useState<string | null>(null);
  useEffect(() => {
    if (returnTo && state.openCertificateId === null) {
      document.querySelector<HTMLAnchorElement>(`a.cert-mark[href="#cert-${returnTo}"]`)?.focus();
      setReturnTo(null);
    }
  }, [returnTo, state.openCertificateId]);
  const openCertificate = (id: string): void => {
    setReturnTo(id);
    store.update({ openCertificateId: id, reviewDraft: false });
  };
  /* Cancel / save return focus to #record-review AFTER the tree commits — a
     pass into Phase 6 unmounts the button (no gate follows), so the fallback
     is Change phase… (review round 1) */
  const [focusBack, setFocusBack] = useState(false);
  useEffect(() => {
    if (focusBack && !state.reviewDraft) {
      (document.getElementById("record-review") ?? document.getElementById("change-phase"))?.focus();
      setFocusBack(false);
    }
  }, [focusBack, state.reviewDraft]);
  const closeSheet = (): void => {
    setFocusBack(true);
    store.update({ reviewDraft: false, reviewGateId: null });
  };
  /* a correction opens the sheet for an EARLIER gate (PP10): its phase is
     that gate's, so the rows, criteria and the reviews to supersede are the
     ones that gate decided — the declared phase's sheet cannot reach them */
  const sheetGate = state.reviewGateId === null ? gate : state.pdp.gates.find((g) => g.id === state.reviewGateId);
  const sheetPhase = state.reviewGateId === null ? phase : (sheetGate?.afterPhase ?? null);
  if (state.openCertificateId)
    return <CertificatePage product={product} pdp={pdp} certId={state.openCertificateId} state={state} onBack={() => store.update({ openCertificateId: null })} />;
  if (state.reviewDraft && phase !== null && sheetPhase !== null && sheetGate)
    return <ReviewSheet product={product} pdp={pdp} phase={sheetPhase} declaredPhase={phase} gate={sheetGate} state={state} store={store} onClose={closeSheet} />;
  return (
    <section className="area-panel pdp-phase" aria-label={`${product.name} — PDP phase`}>
      <Stepper phase={phase} gates={state.pdp.gates} certificates={inForce(pdp.certificates)} onOpenCertificate={openCertificate} />
      {phase === null && (
        <p className="pdp-undeclared">
          {decl?.kind === "not-in-pdp" ? (
            <>
              <span className="state not-in-pdp">not in the PDP</span> {decl.reason} — no phase, nothing owed.
            </>
          ) : (
            <>
              PDP phase <Missing /> — nothing is owed until someone declares one with <b>Change phase…</b>
            </>
          )}
        </p>
      )}
      {phase !== null && (
        <>
          <h3 className="pdp-h">Phase complete</h3>
          <PhaseBars
            bars={phaseBars(state.pdp.deliverables, pdp)}
            onOpenPhase={(p) => store.update({ area: "Deliverables", deliverablesPhase: p, deliverablesState: "all", deliverablesSearch: "" })}
          />
          <h3 className="pdp-h">Exit criteria · Phase {phase}</h3>
          <ul className="slots pdp-criteria">
            {criteria.map((c) => (
              <li key={c.id}>
                <div className="slot-name">{c.area}</div>
                <div className="val">
                  {c.text ?? <Missing />}
                  {c.unconfirmed && <span className="state unconfirmed">unconfirmed</span>}
                </div>
                <CriterionLine criterion={c} owed={owed} phase={phase} state={state} />
              </li>
            ))}
          </ul>
          {gate && (
            <p className="gate-ahead">
              Gate ahead: {gate.label} · approver {gate.approverRoleId ? roleName(state.roles, gate.approverRoleId) : <Missing />}
            </p>
          )}
        </>
      )}
      <h3 className="pdp-h">Gate history</h3>
      <GateHistory pdp={pdp} state={state} onOpenCertificate={openCertificate} onSupersede={(gateId) => store.update({ reviewDraft: true, reviewGateId: gateId })} />
    </section>
  );
}

/* every review, newest first: `{gate} · {decision} {date} · approver: {role}
   · [minutes] · {k} open gaps stamped`, the certificate mark when one was
   issued, the superseded chip when a later review replaced it (PP10) */
function GateHistory({
  pdp,
  state,
  onOpenCertificate,
  onSupersede,
}: {
  pdp: ProductPdp;
  state: ClientState;
  onOpenCertificate: (id: string) => void;
  /* opens the sheet for the review's gate — the correction path (PP10) */
  onSupersede: (gateId: string) => void;
}): JSX.Element {
  if (pdp.reviews.length === 0) return <p className="slots gate-history">No gate reviews recorded.</p>;
  const gapsOf = (r: GateReview): string => `${r.openGaps.length} open gap${r.openGaps.length === 1 ? "" : "s"} stamped`;
  return (
    <ul className="slots gate-history">
      {[...pdp.reviews].reverse().map((r) => {
        const gate = state.pdp.gates.find((g) => g.id === r.gateId);
        const cert = pdp.certificates.find((c) => c.gateReviewId === r.id);
        return (
          <li key={r.id}>
            {gate?.label ?? r.gateId} · {DECISION_LABEL[r.decision]} {fmtDay(r.reviewDate)} · approver:{" "}
            {gate?.approverRoleId ? roleName(state.roles, gate.approverRoleId) : <Missing />}
            {r.minutesUrl && (
              <>
                {" · "}
                <a href={r.minutesUrl} target="_blank" rel="noopener">
                  minutes
                </a>
              </>
            )}
            {" · "}
            {gapsOf(r)}
            {cert && (
              <>
                {" "}
                <CertMark id={cert.id} onOpen={onOpenCertificate} />
              </>
            )}
            {r.supersededBy && <span className="state superseded">superseded</span>}
            {r.supersededBy === null && (
              <button type="button" className="btn ghost supersede-review" onClick={() => onSupersede(r.gateId)}>
                Supersede…
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* the certificate mark: an anchor to the certificate's page, solid accent */
function CertMark({ id, onOpen }: { id: string; onOpen: (id: string) => void }): JSX.Element {
  return (
    <a
      className="state cert-mark"
      href={`#cert-${id}`}
      onClick={(e) => {
        e.preventDefault();
        onOpen(id);
      }}
    >
      certificate
    </a>
  );
}

/* The review sheet's owner (Screen 5): the declared phase's bar and owed
   rows, the gate's in-force earlier reviews to supersede, the POST; the
   server's refusal (a non-Admin's 403) lands in the sheet's alert. A
   non-Admin reads it read-only — the recording gate is server-side either
   way. Cancel / save return focus to #record-review. */
function ReviewSheet({
  product,
  pdp,
  phase,
  declaredPhase,
  gate,
  state,
  store,
  onClose,
}: {
  product: Product;
  pdp: ProductPdp;
  /* the phase this sheet decides — the gate's, not always the declared one */
  phase: PdpPhase;
  declaredPhase: PdpPhase;
  gate: PdpGate;
  state: ClientState;
  store: Store;
  onClose: () => void;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [refusals, setRefusals] = useState(0);
  const [saving, setSaving] = useState(false);
  /* every owed row of phases 0..N — what the certificate freezes and what the
     server stamps as open gaps (PP7); an earlier phase's row names its phase */
  const owed = slotRows(owedRows(state.pdp.deliverables, phase), pdp.evidence);
  const rows: ReviewRow[] = owed.map((r) => ({
    key: slotKey(r),
    name: `${r.row.name}${r.repeat ? ` (Phase ${r.phase} repeat)` : r.phase !== phase ? ` (Phase ${r.phase})` : ""}`,
    state: r.state,
    ...(r.current?.kind === "estimate" ? { percent: r.current.percent } : {}),
  }));
  const bar = phaseBars(state.pdp.deliverables, pdp).find((b) => b.phase === phase)!;
  const criteria = state.pdp.criteria.filter((c) => c.phase === phase);
  /* each criterion's linked reading — what the certificate freezes (KF13/KF15) */
  const criterionLines = Object.fromEntries(criteria.map((c) => [c.id, <CriterionLine key={c.id} criterion={c} owed={owed} phase={phase} state={state} />]));
  const gates = state.pdp.gates.filter((g) => g.afterPhase === phase);
  const earlier = pdp.reviews
    .filter((r) => gates.some((g) => g.id === r.gateId) && r.supersededBy === null)
    .map((r) => ({ id: r.id, decision: r.decision, reviewDate: r.reviewDate }));
  const close = (): void => {
    onClose();
  };
  const save = async (input: GateReviewInput): Promise<void> => {
    setSaving(true);
    const refused = await recordGateReview(store, product.id, input);
    setSaving(false);
    if (refused) {
      setError(refused);
      setRefusals((n) => n + 1);
    } else close();
  };
  return (
    <GateReviewSheet
      productName={product.name}
      phase={phase}
      declaredPhase={declaredPhase}
      bar={bar}
      rows={rows}
      gapCounts={owedCounts(owed)}
      criteria={criteria}
      criterionLines={criterionLines}
      gates={gates}
      approverRoleName={gate.approverRoleId ? roleName(state.roles, gate.approverRoleId) : null}
      earlierReviews={earlier}
      now={new Date().toISOString()}
      today={utcToday()}
      error={error}
      errorNonce={refusals}
      saving={saving}
      readOnly={!state.catalogAuthority}
      onSave={(input) => void save(input)}
      onCancel={close}
    />
  );
}

/* The certificate page's owner (Screen 6): fetched on open (PP1 — heads
   ride the record, the body is read when a reader asks for it), the paper
   with Back and Print, and the honest failure with the same way back. */
function CertificatePage({ product, pdp, certId, state, onBack }: { product: Product; pdp: ProductPdp; certId: string; state: ClientState; onBack: () => void }): JSX.Element {
  const [cert, setCert] = useState<PhaseCertificate | null | "failed">(null);
  useEffect(() => {
    let live = true;
    void fetchCertificate(product.id, certId).then((got) => {
      if (live) setCert(got ?? "failed");
    });
    return () => {
      live = false;
    };
  }, [product.id, certId]);
  if (cert === null)
    return (
      <section className="area-panel cert-page">
        <p className="slot-note">Loading certificate…</p>
      </section>
    );
  if (cert === "failed")
    return (
      <section className="area-panel cert-page">
        <p className="form-error" role="alert">
          Couldn't load the certificate — is the server healthy?
        </p>
        <button type="button" id="cert-back" className="btn ghost" onClick={onBack}>
          ← Back to Phase
        </button>
      </section>
    );
  const review = pdp.reviews.find((r) => r.id === cert.gateReviewId);
  const gate = state.pdp.gates.find((g) => g.id === review?.gateId);
  return (
    <Certificate
      cert={cert}
      gateLabel={gate?.label ?? `Phase ${cert.phase} exit`}
      approverRoleName={cert.frozen.approverRoleId ? roleName(state.roles, cert.frozen.approverRoleId) : null}
      roleNames={Object.fromEntries(state.roles.map((r) => [r.id, r.name]))}
      feedSourceLabels={Object.fromEntries(state.feedSources.map((f) => [f.id, f.label]))}
      onBack={onBack}
    />
  );
}

/* seven steps, the declared one current; a diamond at every gate boundary,
   named in the preceding step's visible text; visually-hidden state words for
   screen readers (UI/UX review) */
function Stepper({
  phase,
  gates,
  certificates,
  onOpenCertificate,
}: {
  phase: PdpPhase | null;
  gates: PdpGate[];
  /* the in-force certificates: a passed phase's bubble is ✓ and carries the mark */
  certificates: CertificateHead[];
  onOpenCertificate: (id: string) => void;
}): JSX.Element {
  const status = (p: PdpPhase): "done" | "current" | "ahead" =>
    phase === null || p > phase ? "ahead" : p === phase ? "current" : "done";
  const diamond = (p: PdpPhase): "passed" | "ahead" | "later" =>
    phase === null || p > phase ? "later" : p === phase ? "ahead" : "passed";
  const words = { done: "completed", current: "current", ahead: "ahead" } as const;
  return (
    <ol className="pdp-stepper" aria-label="PDP phases">
      {PDP_PHASES.map((p) => {
        const st = status(p);
        const gate = gateAfter(gates, p);
        const cert = certificates.find((c) => c.phase === p);
        return (
          <li key={p} className={`pstep ${st}`} aria-current={st === "current" ? "step" : undefined}>
            <span className="step-bub" aria-hidden="true">
              {cert ? "✓" : p}
            </span>
            <span className="pstep-text">
              Phase {p} · {PDP_PHASE_NAMES[p]}
              {gate && <span className="pstep-gate"> · exit at {gate.label}</span>}
            </span>
            <span className="sr-only">{words[st]}</span>
            {cert && <CertMark id={cert.id} onOpen={onOpenCertificate} />}
            {gate && (
              <span className={`gate-diamond ${diamond(p)}`} aria-hidden="true">
                ◆
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* a criterion's linked feed slot's reading, without a button (KF12: the
   Deliverables row reads; here the page shows what it holds) — the phase's
   own slot first, else an earlier iterate slot naming it (PP9's rule) */
function CriterionLine({ criterion, owed, phase, state }: { criterion: PdpCriterion; owed: SlotRow[]; phase: PdpPhase; state: ClientState }): JSX.Element | null {
  const named = feedSlotFor(owed, criterion.id, phase);
  if (!named || named.current?.kind !== "feed") return null;
  return <FeedCell r={named} ev={named.current} state={state} />;
}

/* ======================= Deliverables ======================= */

const names = (roles: Role[], ids: string[]): JSX.Element =>
  ids.length ? <>{ids.map((id) => roleName(roles, id)).join(" / ")}</> : <Missing />;

const STATES = ["all", "missing", "estimated", "unconfirmed", "evidence"] as const;

/* the PP18 stamp under an evidence value: `recorded by {name} · {date}` —
   the import stamp has no person, so it reads `recorded · {date}` */
const recordedLine = (ev: Evidence): string =>
  `recorded${ev.recorded.by ? ` by ${ev.recorded.by.name}` : ""} · ${fmtStampDate(ev.recorded.date)}`;

const sourceLabel = (sources: FeedSourceInfo[], sourceId: string): string => sources.find((s) => s.id === sourceId)?.label ?? sourceId;

function DeliverablesArea({ product, pdp, state, store }: { product: Product; pdp: ProductPdp; state: ClientState; store: Store }): JSX.Element {
  const decl = pdp.declaration;
  const phase = declaredPhase(decl);
  /* the row buttons a closing drawer hands focus back to */
  const table = useRef<HTMLDivElement>(null);
  const [returnTo, setReturnTo] = useState<SlotKey | null>(null);
  useEffect(() => {
    if (returnTo && state.evidenceDraft === null) {
      /* the row may have left the filtered set (a red row recorded under the
         "not recorded" chip) — then the search box takes the focus */
      const target =
        table.current?.querySelector<HTMLButtonElement>(`tr[data-slot="${returnTo}"] .deliv-record`) ??
        document.querySelector<HTMLInputElement>("#deliv-search");
      target?.focus();
      setReturnTo(null);
    }
  }, [returnTo, state.evidenceDraft]);
  if (phase === null)
    return (
      <section className="area-panel pdp-deliverables">
        <p className="recs-empty">
          {decl?.kind === "not-in-pdp"
            ? "Not in the PDP — nothing owed."
            : "Declare a phase on the Phase area; nothing is owed until then."}
        </p>
      </section>
    );
  const all = slotRows(owedRows(state.pdp.deliverables, phase), pdp.evidence);
  const groups = groupOwedRows(
    all,
    { phase: state.deliverablesPhase, state: state.deliverablesState, search: state.deliverablesSearch },
    (id) => roleName(state.roles, id),
  );
  /* the counts follow the phase chip alone, so the line agrees with that phase's bar */
  const counts = owedCounts(state.deliverablesPhase === "all" ? all : all.filter((r) => r.phase === state.deliverablesPhase));
  const draft = state.evidenceDraft === null ? undefined : all.find((r) => slotKey(r) === state.evidenceDraft);
  const openDrawer = (key: SlotKey): void => {
    setReturnTo(key);
    store.update({ evidenceDraft: key });
  };
  const closeDrawer = (): void => store.update({ evidenceDraft: null });
  return (
    <section className="area-panel pdp-deliverables">
      <div className="dash-filters">
        <div className="seg-ctl" role="group" aria-label="Phase">
          <button type="button" id="deliv-phase-all" aria-pressed={state.deliverablesPhase === "all"} onClick={() => store.update({ deliverablesPhase: "all" })}>
            All owed
          </button>
          {PDP_PHASES.filter((p) => p <= phase).map((p) => (
            <button key={p} type="button" id={`deliv-phase-${p}`} aria-pressed={state.deliverablesPhase === p} onClick={() => store.update({ deliverablesPhase: p })}>
              Phase {p}
            </button>
          ))}
        </div>
        <div className="seg-ctl" role="group" aria-label="State">
          {STATES.map((st) => (
            <button key={st} type="button" id={`deliv-state-${st}`} aria-pressed={state.deliverablesState === st} onClick={() => store.update({ deliverablesState: st })}>
              {st === "all" ? "All states" : STATE_LABEL[st]}
            </button>
          ))}
        </div>
        <label className="sr-only" htmlFor="deliv-search">
          Filter deliverables
        </label>
        <input
          id="deliv-search"
          className="dash-search"
          type="search"
          placeholder="Filter by name, path, role…"
          value={state.deliverablesSearch}
          onChange={(e) => store.update({ deliverablesSearch: e.target.value })}
        />
      </div>
      <CountsLine counts={counts} />
      <div className="dash-table" ref={table}>
        <table id="deliverables-table" className="recs">
          <thead>
            <tr>
              <th>Deliverable</th>
              <th>Phase</th>
              <th>Owner → Reviewer</th>
              <th>State</th>
              <th>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 && (
              <tr>
                <td colSpan={5} className="recs-empty">
                  No owed rows match these filters.
                </td>
              </tr>
            )}
            {groups.map((g) => (
              <GroupRows key={g.swimlane} group={g} state={state} store={store} retired={product.retired === true} certificates={pdp.certificates} onRecord={openDrawer} />
            ))}
          </tbody>
        </table>
      </div>
      {draft && <EvidenceDrawer key={slotKey(draft)} product={product} row={draft} state={state} store={store} onClose={closeDrawer} />}
    </section>
  );
}

function CountsLine({ counts }: { counts: OwedCounts }): JSX.Element {
  return (
    <p className="deliv-counts">
      {counts.owed} owed · {counts.evidenced} evidenced · {counts.estimated} estimated ·{" "}
      <span className="state missing">{counts.missing} not recorded</span>
      {counts.unconfirmed > 0 && (
        <>
          {" · "}
          <span className="state unconfirmed">{counts.unconfirmed} unconfirmed</span>
        </>
      )}
    </p>
  );
}

interface RowsProps {
  state: ClientState;
  /* the feed cell's Read / Refresh / Retry posts through it (KF12) */
  store: Store;
  retired: boolean;
  /* the in-force certificates, for PP17's "changed since" mark */
  certificates: CertificateHead[];
  onRecord: (key: SlotKey) => void;
}

/* PP17, derived on the client from the heads alone: the latest in-force
   certificate covering the slot's phase; a revision recorded after its
   issue means the row changed since (replaced, estimated or cleared) */
export function changedSince(r: SlotRow, certificates: CertificateHead[]): CertificateHead | null {
  const covering = certificates.filter((c) => c.supersededBy === null && c.phase >= r.phase).sort((a, b) => (a.issuedAt < b.issuedAt ? 1 : -1))[0];
  if (!covering) return null;
  const last = r.history.at(-1);
  return last && last.recorded.date > covering.issuedAt ? covering : null;
}

function GroupRows({ group, ...rest }: RowsProps & { group: ReturnType<typeof groupOwedRows>[number] }): JSX.Element {
  return (
    <>
      <tr className="group-row">
        <td colSpan={5}>{group.swimlane}</td>
      </tr>
      {group.paths.map((p) => (
        <PathRows key={p.path} path={p.path} rows={p.rows} {...rest} />
      ))}
    </>
  );
}

function PathRows({ path, rows, ...rest }: RowsProps & { path: string; rows: SlotRow[] }): JSX.Element {
  return (
    <>
      <tr className="path-row">
        <td colSpan={5}>{path}</td>
      </tr>
      {rows.map((r) => (
        <SlotRowView key={slotKey(r)} r={r} {...rest} />
      ))}
    </>
  );
}

function SlotRowView({ r, state, store, retired, certificates, onRecord }: RowsProps & { r: SlotRow }): JSX.Element {
  const key = slotKey(r);
  const [historyOpen, setHistoryOpen] = useState(false);
  const since = changedSince(r, certificates);
  return (
    <>
      <tr className="deliv-row" data-slot={key}>
        <td className="deliv-name">
          {r.row.name}
          {r.repeat ? ` (Phase ${r.phase} repeat)` : ""}
        </td>
        <td className="deliv-phase">{r.phase}</td>
        <td className="deliv-roles">
          {names(state.roles, r.row.ownerRoleIds)} → {names(state.roles, r.row.reviewerRoleIds)}
        </td>
        <td>
          <StateChip state={r.state} percent={r.current?.kind === "estimate" ? r.current.percent : undefined} />
          {since && (
            <>
              {" "}
              <span className="state since-cert">changed</span>
              <span className="slot-note">changed since Phase {since.phase} certificate</span>
            </>
          )}
        </td>
        <td className="deliv-evidence">
          <EvidenceCell r={r} state={state} store={store} />
          <div className="deliv-actions">
            {!retired && (
              <button type="button" className="btn small deliv-record" onClick={() => onRecord(key)}>
                {r.current ? "Replace…" : "Record evidence…"}
              </button>
            )}
            {r.history.length > 0 && (
              <button type="button" className="btn ghost small deliv-history" aria-expanded={historyOpen} onClick={() => setHistoryOpen((o) => !o)}>
                History
              </button>
            )}
          </div>
        </td>
      </tr>
      {historyOpen && (
        <tr className="history-row">
          <td colSpan={5}>
            <ul className="slots slot-history">
              {[...r.history].reverse().map((ev, i) => (
                <li key={i}>{historyLine(ev, state)}</li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

/* the standing value: a link title as a new-tab link, an estimate's note in
   quotes, or a feed pointer as its source · measure with the reading the page
   holds — the logged one on load, the answer of the row's own Read / Refresh
   / Retry after (KF12; KF14: a reading never changes the state) — each with
   its stamp; a slot with nothing standing reads `—`. The target is the linked
   catalog criterion's, never a product-side number (KF13). A retired product
   still reads: a measurement is not a write to the record. */
function EvidenceCell({ r, state, store }: { r: SlotRow; state: ClientState; store: Store }): JSX.Element {
  const ev = r.current;
  if (!ev) return <>—</>;
  const unconfirmed = (ev.kind === "link" || ev.kind === "feed") && ev.unconfirmed;
  return (
    <>
      {ev.kind === "link" && (
        <a href={ev.url} target="_blank" rel="noopener">
          {ev.title}
        </a>
      )}
      {ev.kind === "estimate" && <span className="estimate-note">“{ev.note}”</span>}
      {ev.kind === "feed" && <FeedCell r={r} ev={ev} state={state} onRead={(ref, rowName, target) => void readFeed(store, { ref, rowName, target })} />}
      {unconfirmed && (
        <span className="deliv-why">
          {" "}
          <span className="state unconfirmed">unconfirmed</span> — {ev.why}
        </span>
      )}
      <span className="slot-note">{recordedLine(ev)}</span>
    </>
  );
}

/* the feed cell as the page holds it: the ref's reading (or `not read yet`
   before anything is known) with the linked criterion's target */
function FeedCell({
  r,
  ev,
  state,
  onRead,
}: {
  r: SlotRow;
  ev: Extract<Evidence, { kind: "feed" }>;
  state: ClientState;
  /* absent = no button (the Phase area's and the sheet's criteria lines) */
  onRead?: (ref: FeedRef, rowName: string, target: FeedTarget | null) => void;
}): JSX.Element {
  const held = state.feedReadings[feedKey(ev.ref)];
  const target = state.pdp.criteria.find((c) => c.id === ev.criterionId)?.target ?? null;
  return (
    <FeedRowValue
      slotId={slotKey(r)}
      rowName={r.row.name}
      sourceLabel={`${sourceLabel(state.feedSources, ev.ref.sourceId)} · ${ev.ref.measure}`}
      reading={held?.reading ?? { state: "not-read-yet" }}
      target={target}
      onRead={onRead ? () => onRead(ev.ref, r.row.name, target) : undefined}
      inFlight={held?.inFlight}
      retriedAt={held?.retriedAt}
      httpError={held?.httpError}
    />
  );
}

/* one history entry: what was recorded, by whom, when */
function historyLine(ev: Evidence, state: ClientState): string {
  const by = ev.recorded.by ? ` · by ${ev.recorded.by.name}` : "";
  const stamp = `${by} · ${fmtStampDate(ev.recorded.date)}`;
  const flag = (ev.kind === "link" || ev.kind === "feed") && ev.unconfirmed ? ` · unconfirmed — ${ev.why}` : "";
  switch (ev.kind) {
    case "link":
      return `Link: ${ev.title} — ${ev.url}${flag}${stamp}`;
    case "feed": {
      const crit = state.pdp.criteria.find((c) => c.id === ev.criterionId);
      return `Feed: ${sourceLabel(state.feedSources, ev.ref.sourceId)} · ${ev.ref.measure}${crit ? ` · vs ${crit.area}` : ""}${flag}${stamp}`;
    }
    case "estimate":
      return `Estimate ${ev.percent}% — “${ev.note}”${stamp}`;
    default:
      return `Cleared${stamp}`;
  }
}

/* the Record evidence drawer (Screen 4): a modal side drawer over the form;
   Tab wraps inside it, Escape and Cancel close it, the owner returns focus
   to the row's button. The PUT / DELETE answer the whole record. */
function EvidenceDrawer({
  product,
  row,
  state,
  store,
  onClose,
}: {
  product: Product;
  row: SlotRow;
  state: ClientState;
  store: Store;
  onClose: () => void;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  /* counts refusals, so the SAME reason twice re-focuses and re-announces */
  const [refusals, setRefusals] = useState(0);
  const [saving, setSaving] = useState(false);
  const aside = useRef<HTMLElement>(null);
  const key = slotKey(row);

  const finish = (refused: string | null): void => {
    setSaving(false);
    if (refused) {
      setError(refused);
      setRefusals((n) => n + 1);
    } else onClose();
  };
  const save = async (input: EvidenceInput): Promise<void> => {
    setSaving(true);
    finish(await recordEvidence(store, product.id, key, input));
  };
  const clear = async (): Promise<void> => {
    setSaving(true);
    finish(await clearEvidence(store, product.id, key));
  };
  /* Tab wraps inside the drawer. Of a radio group only the checked member is
     in the tab order, so the unchecked ones are not stops here either. */
  const trap = (e: KeyboardEvent<HTMLElement>): void => {
    if (e.key !== "Tab" || !aside.current) return;
    const focusable = [...aside.current.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href]")].filter(
      (el) => !(el instanceof HTMLInputElement && el.type === "radio" && !el.checked && el.form?.querySelector(`input[name="${el.name}"]:checked`)),
    );
    /* the form always renders its Cancel button, so both ends exist */
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  const criteria: PdpCriterion[] = state.pdp.criteria;
  return (
    <>
      {/* the modal's backdrop: the page behind is not clickable while the drawer is open; a click on it cancels */}
      <div className="side-drawer-backdrop" onClick={onClose} />
      <aside id="evidence-drawer" className="side-drawer open" role="dialog" aria-modal="true" aria-labelledby="evidence-title" ref={aside} onKeyDown={trap}>
        <EvidenceForm
          slot={{
            name: row.row.name,
            swimlane: row.row.swimlane,
            path: row.row.path,
            phase: row.phase,
            ownerRoleNames: row.row.ownerRoleIds.map((id) => roleName(state.roles, id)),
            reviewerRoleNames: row.row.reviewerRoleIds.map((id) => roleName(state.roles, id)),
          }}
          current={row.current}
          feedSources={state.feedSources}
          criteria={criteria}
          error={error}
          errorNonce={refusals}
          saving={saving}
          onSave={(input) => void save(input)}
          onClear={() => void clear()}
          onCancel={onClose}
        />
      </aside>
    </>
  );
}
